/**
 * Field definitions for the mixed-import (item cleanup v2) prototype.
 *
 * Differs from the food and packaging field configs in two ways:
 *   1. The 'unit' field is a single free-text input (no select), because a
 *      mixed file can contain both mass/volume units (kg, l) and pack
 *      descriptors (case of 12, 4 x 500g). Per-row input_mode is inferred
 *      server-side from this string using the same alias rule as the
 *      frontend's existing unitAliases map.
 *   2. There is no separate totalWeight column — quantity covers both
 *      "pack count" and "total weight in unit" cases. The processor
 *      disambiguates per row.
 *
 * The exclude* flags mirror food-import/fields.ts: when the user has set
 * a single site / single period for the whole import on the period page,
 * those columns are dropped from the spreadsheet importer.
 */
import { useFormattedSites } from '@app/ccf/company-report/utils';
import { useGetSites } from '@shared/api';
import { useCallback } from 'react';
import {
  FieldMatchSelectValue,
  Fields,
} from 'react-spreadsheet-import/types/types';

const maxLengthValidation = (length: number) => ({
  rule: 'regex',
  value: `^.{0,${length}}$`,
  errorMessage: `Must not be longer than ${length} characters`,
  level: 'error',
});

const requiredValidation = (name: string) => ({
  rule: 'required',
  errorMessage: `${name} is required`,
  level: 'error',
});

export const useGetCleanupMixedFields: (options?: {
  excludeDate?: boolean;
  excludeSite?: boolean;
  // Supplier is a purchased-goods concept; the sold importer swaps it for
  // Customer via excludeSupplier + includeCustomer.
  excludeSupplier?: boolean;
  includeCustomer?: boolean;
  // Total spend isn't fully supported for sold goods yet, so the sold importer
  // drops the column to avoid implying it's captured. Purchased keeps it.
  excludeTotalSpend?: boolean;
}) => Fields<string> = (options = {}) => {
  const { data: sites } =
    useGetSites({
      page: 1,
      page_size: 1000,
    }) || [];
  const formattedSites = useFormattedSites(sites?.results || []);

  const matchSiteValues: FieldMatchSelectValue = useCallback(
    (record, fieldOptions) => {
      const option = fieldOptions.find(
        (o) => o.label.toLowerCase() === record.entry.toLowerCase()
      );
      if (option) return { ...record, value: option.value };
      return record;
    },
    []
  );

  const allFields = [
    {
      label: 'Name',
      key: 'name',
      alternateMatches: ['title', 'description'],
      fieldType: { type: 'input' },
      example: 'Cherry tomatoes 12oz paper cup',
      validations: [requiredValidation('Name'), maxLengthValidation(240)],
    },
    {
      label: 'Quantity',
      key: 'quantity',
      alternateMatches: ['qty', 'amount'],
      fieldType: { type: 'input' },
      example: '320',
      validations: [requiredValidation('Quantity'), maxLengthValidation(36)],
    },
    {
      label: 'Unit',
      key: 'unit',
      alternateMatches: ['uom', 'units'],
      fieldType: { type: 'input' },
      example: 'kg or 4 x 500g',
      validations: [maxLengthValidation(36)],
    },
    {
      label: 'ID',
      key: 'remoteId',
      alternateMatches: ['product id', 'sku', 'id'],
      fieldType: { type: 'input' },
      example: '#HK-TOM-1',
      validations: [maxLengthValidation(240)],
    },
    {
      label: 'Supplier',
      key: 'supplier',
      alternateMatches: ['supplier', 'vendor', 'manufacturer'],
      fieldType: { type: 'input' },
      example: 'HK Foods',
      validations: [maxLengthValidation(240)],
    },
    {
      label: 'Customer',
      key: 'customer',
      alternateMatches: ['customer', 'client', 'buyer'],
      fieldType: { type: 'input' },
      example: 'HK Foods',
      validations: [maxLengthValidation(240)],
    },
    {
      label: 'Site',
      key: 'site',
      alternateMatches: ['location', 'place'],
      fieldType: {
        type: 'select',
        options: formattedSites,
      },
      matchSelectValue: matchSiteValues,
      example: '23 Mill Lane',
    },
    {
      label: 'Date',
      key: 'date',
      alternateMatches: ['date'],
      fieldType: { type: 'input' },
      example: '01/10/2023',
      validations: options.excludeDate ? [] : [requiredValidation('Date')],
    },
    {
      label: 'Total spend',
      key: 'totalSpend',
      alternateMatches: ['cost', 'spend', 'total spend', 'total cost', 'price'],
      fieldType: { type: 'input' },
      example: '250.00',
      validations: [maxLengthValidation(36)],
    },
  ];

  return allFields.filter((field) => {
    if (options.excludeDate && field.key === 'date') return false;
    if (options.excludeSite && field.key === 'site') return false;
    if (options.excludeSupplier && field.key === 'supplier') return false;
    if (!options.includeCustomer && field.key === 'customer') return false;
    if (options.excludeTotalSpend && field.key === 'totalSpend') return false;
    return true;
  });
};
